/**
 * RubricScorecard
 *
 * Shared analysis presentation: horizontal progress bars on top for at-a-glance
 * strong-vs-weak scanning, plus expandable per-category cards with feedback
 * detail. Color tiers: green ≥ 90, amber 60–89, red < 60.
 *
 * @module RubricScorecard
 */

import { useState, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

export type ScoreTier = "strong" | "okay" | "weak" | "neutral";

export interface ScorecardCategory {
  readonly key: string;
  readonly label: string;
  /** 0-100 (rounded). Null hides the score badge. */
  readonly score: number | null;
  /** 0-1 fraction. Null hides weight. */
  readonly weight: number | null;
  /** Optional leading icon for the category card. */
  readonly icon?: ReactNode;
  /** Body content rendered inside the expanded card. */
  readonly body?: ReactNode;
  /** Hide the bar row for this category (still appears as a card). */
  readonly excludeFromBars?: boolean;
  /** Force the card to start expanded (default closed). */
  readonly defaultOpen?: boolean;
}

export interface RubricScorecardProps {
  readonly categories: readonly ScorecardCategory[];
  readonly className?: string;
  readonly title?: string;
  readonly description?: string;
  /** Weighted composite final score (0-100). Shown as the last bar. */
  readonly finalScore?: number | null;
}

const getTier = (score: number | null): ScoreTier => {
  if (score === null || !Number.isFinite(score)) return "neutral";
  if (score >= 90) return "strong";
  if (score >= 60) return "okay";
  return "weak";
};

const TIER_STYLES: Record<
  ScoreTier,
  {
    readonly bar: string;
    readonly badge: string;
    readonly badgeText: string;
  }
> = {
  strong: {
    bar: "bg-emerald-500",
    badge: "bg-emerald-100 text-emerald-700",
    badgeText: "text-emerald-700",
  },
  okay: {
    bar: "bg-amber-500",
    badge: "bg-amber-100 text-amber-700",
    badgeText: "text-amber-700",
  },
  weak: {
    bar: "bg-rose-500",
    badge: "bg-rose-100 text-rose-700",
    badgeText: "text-rose-700",
  },
  neutral: {
    bar: "bg-muted-foreground/40",
    badge: "bg-muted text-muted-foreground",
    badgeText: "text-muted-foreground",
  },
};

const formatWeight = (weight: number | null): string | null => {
  if (weight === null || !Number.isFinite(weight)) return null;
  return `${Math.round(weight * 100)}%`;
};

const formatScore = (score: number | null): string | null => {
  if (score === null || !Number.isFinite(score)) return null;
  return `${Math.round(score)}%`;
};

interface ScoreBadgeProps {
  readonly score: number | null;
  readonly tier: ScoreTier;
  readonly className?: string;
}

function ScoreBadge({ score, tier, className }: ScoreBadgeProps) {
  const label = formatScore(score) ?? "—";
  return (
    <span
      className={cn(
        "font-ui inline-flex min-w-[3rem] items-center justify-center rounded-md px-2 py-1 text-xs font-semibold",
        TIER_STYLES[tier].badge,
        className,
      )}
    >
      {label}
    </span>
  );
}

interface BarRowProps {
  readonly label: string;
  readonly score: number | null;
  readonly weight: number | null;
  readonly tier: ScoreTier;
}

function BarRow({ label, score, weight, tier }: BarRowProps) {
  const safeScore =
    typeof score === "number" && Number.isFinite(score)
      ? Math.max(0, Math.min(100, score))
      : 0;
  const scoreText = formatScore(score);
  const weightText = formatWeight(weight);
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-2 sm:grid-cols-[minmax(7rem,9rem)_minmax(0,1fr)_auto] sm:gap-3">
      <div className="font-ui min-w-0 truncate text-sm text-foreground/80">
        {label}
      </div>
      <div
        className="relative col-span-2 row-start-2 h-2 w-full overflow-hidden rounded-full bg-muted sm:col-span-1 sm:row-auto"
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(safeScore)}
      >
        <div
          className={cn(
            "absolute inset-y-0 left-0 rounded-full transition-all",
            TIER_STYLES[tier].bar,
          )}
          style={{ width: `${safeScore}%` }}
        />
      </div>
      <div className="font-ui flex shrink-0 items-baseline gap-1 text-right text-sm tabular-nums">
        <span className="font-semibold text-foreground">
          {scoreText ?? "—"}
        </span>
        {weightText ? (
          <span className="text-xs text-muted-foreground">· {weightText}</span>
        ) : null}
      </div>
    </div>
  );
}

interface CategoryCardProps {
  readonly category: ScorecardCategory;
  readonly tier: ScoreTier;
}

function CategoryCard({ category, tier }: CategoryCardProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(category.defaultOpen ?? false);
  const hasBody = Boolean(category.body);
  const weightText = formatWeight(category.weight);

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div
        className={cn(
          "rounded-lg border border-border bg-card transition-colors",
          open && "shadow-sm",
        )}
      >
        <CollapsibleTrigger
          disabled={!hasBody}
          className={cn(
            "group flex w-full items-center gap-3 px-4 py-3 text-left",
            hasBody
              ? "cursor-pointer hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              : "cursor-default",
            "rounded-lg",
          )}
          aria-label={t("simulation.results.scorecard.toggleCategory", {
            category: category.label,
            defaultValue: `Toggle ${category.label} details`,
          })}
        >
          {category.icon ? (
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-border bg-muted/40 text-muted-foreground">
              {category.icon}
            </span>
          ) : null}
          <span className="font-ui flex-1 truncate text-sm font-semibold text-foreground">
            {category.label}
          </span>
          <ScoreBadge score={category.score} tier={tier} />
          {weightText ? (
            <span className="font-ui hidden text-xs text-muted-foreground sm:inline">
              {t("simulation.results.scorecard.weightLabel", {
                percent: weightText,
                defaultValue: `${weightText} weight`,
              })}
            </span>
          ) : null}
          {hasBody ? (
            <ChevronDown
              aria-hidden
              className={cn(
                "h-4 w-4 shrink-0 text-muted-foreground transition-transform",
                open && "rotate-180",
              )}
            />
          ) : (
            <span aria-hidden className="h-4 w-4 shrink-0" />
          )}
        </CollapsibleTrigger>
        {hasBody ? (
          <CollapsibleContent forceMount>
            <div
              className={cn(
                "border-t border-border/60 px-4 py-3",
                !open && "hidden",
              )}
            >
              {category.body}
            </div>
          </CollapsibleContent>
        ) : null}
      </div>
    </Collapsible>
  );
}

export function RubricScorecard({
  categories,
  className,
  title,
  description,
  finalScore = null,
}: RubricScorecardProps) {
  const { t } = useTranslation();
  if (categories.length === 0) return null;

  const barCategories = categories.filter((c) => !c.excludeFromBars);
  const hasFinalScore =
    typeof finalScore === "number" && Number.isFinite(finalScore);
  const finalTier = getTier(finalScore);
  const resolvedTitle =
    title ??
    t("simulation.results.scorecard.title", {
      defaultValue: "Scorecard",
    });
  const resolvedDescription =
    description ??
    t("simulation.results.scorecard.description", {
      defaultValue: "Score · weight per criterion. Tap a row for detail.",
    });

  return (
    <section aria-label={resolvedTitle} className={cn("space-y-4", className)}>
      {barCategories.length > 0 ? (
        <div className="rounded-lg border border-border bg-muted/30 p-4">
          <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
            <h3 className="font-ui text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              {resolvedTitle}
            </h3>
            <p className="font-ui text-xs text-muted-foreground">
              {resolvedDescription}
            </p>
          </div>
          <div className="space-y-2.5">
            {barCategories.map((category) => (
              <BarRow
                key={`bar-${category.key}`}
                label={category.label}
                score={category.score}
                weight={category.weight}
                tier={getTier(category.score)}
              />
            ))}
            {hasFinalScore ? (
              <>
                <div className="my-1 h-px bg-border/60" />
                <div className="grid grid-cols-[minmax(7rem,9rem)_minmax(0,1fr)_auto] items-center gap-3">
                  <div className="font-ui truncate text-sm font-semibold text-foreground">
                    {t("simulation.results.scorecard.finalGrade", {
                      defaultValue: "Final grade",
                    })}
                  </div>
                  <div
                    className="relative h-3 w-full overflow-hidden rounded-full bg-muted"
                    role="progressbar"
                    aria-label={t("simulation.results.scorecard.finalGrade", {
                      defaultValue: "Final grade",
                    })}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={Math.round(finalScore!)}
                  >
                    <div
                      className={cn(
                        "absolute inset-y-0 left-0 rounded-full transition-all",
                        TIER_STYLES[finalTier].bar,
                      )}
                      style={{
                        width: `${Math.max(0, Math.min(100, finalScore!))}%`,
                      }}
                    />
                  </div>
                  <div className="font-ui flex shrink-0 items-baseline gap-1 text-right">
                    <span
                      className={cn(
                        "font-ui inline-flex min-w-[3rem] items-center justify-center rounded-md px-2 py-1 text-sm font-bold",
                        TIER_STYLES[finalTier].badge,
                      )}
                    >
                      {Math.round(finalScore!)}%
                    </span>
                  </div>
                </div>
              </>
            ) : null}
          </div>
        </div>
      ) : null}
      <div className="space-y-2">
        {categories.map((category) => (
          <CategoryCard
            key={`card-${category.key}`}
            category={category}
            tier={getTier(category.score)}
          />
        ))}
      </div>
    </section>
  );
}
